/**
 * The one big live number (docs/market-making-ux.md, Revision 2), shared by the quote builder
 * (Draft) and the tracking card (Working / Exposed / Locked / Settled). Its colour says how
 * settled the number is:
 *
 *   draft     amber, dashed outline: not real yet
 *   working   blue tint with a slow breathing ring (none under reduced motion)
 *   exposed   solid red when the get-out result is a loss, solid green when it isn't
 *   locked    solid green (red for a locked loss)
 *   settled   neutral solid
 *
 * The number flashes (FlashText) whenever it changes.
 */
import type { ReactNode } from "react";
import type { KpiState } from "../../lib/marketMaking.js";
import type { Ccy } from "../../lib/portfolio.js";
import { FlashText } from "../kit.js";
import { signedMoney } from "./shared.js";

const neg = (n: number | null) => n != null && n < -0.005;

/** Box + number colours per state (and sign, for the solid ones). */
export function kpiTone(state: KpiState, value: number | null): { box: string; num: string; chip: string } {
  switch (state) {
    case "draft":
      return { box: "border-2 border-dashed border-amber/60 bg-amber/[0.05]", num: "text-amber", chip: "bg-amber/15 text-amber" };
    case "working":
      return { box: "gb-kpi-working border border-blue/40 bg-blue/[0.08]", num: "text-blue", chip: "bg-blue/15 text-blue" };
    case "exposed":
    case "locked":
      return neg(value)
        ? { box: "border border-down/50 bg-down/15", num: "text-down", chip: "bg-down/15 text-down" }
        : { box: "border border-up/50 bg-up/15", num: "text-up", chip: "bg-up/15 text-up" };
    case "settled":
      return { box: "border border-border-2 bg-raised", num: "text-ink", chip: "bg-faint/15 text-dim" };
  }
}

export function MmKpi({
  state,
  value,
  ccy,
  label,
  suffix,
  caption,
  children,
  className = "",
}: {
  state: KpiState;
  /** Null while it can't be worked out (prices loading, nothing quotes the other side). */
  value: number | null;
  ccy: Ccy;
  /** Before the number: "Get out now:". */
  label?: ReactNode;
  /** After the number: "locked". */
  suffix?: ReactNode;
  /** The one line under the number: "if both sides fill · 2% after fees". */
  caption?: ReactNode;
  /** Secondary lines (small). */
  children?: ReactNode;
  className?: string;
}) {
  const tone = kpiTone(state, value);
  return (
    <div className={`min-w-0 rounded-lg px-4 py-3 ${tone.box} ${className}`} aria-live="polite">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
        {label && <span className={`text-[13px] font-medium ${tone.num}`}>{label}</span>}
        <FlashText value={value}>
          <span className={`nums text-[34px] font-semibold leading-none tracking-tight ${tone.num}`}>{value == null ? "—" : signedMoney(value, ccy)}</span>
        </FlashText>
        {suffix && <span className={`text-[13px] font-medium ${tone.num}`}>{suffix}</span>}
      </div>
      {caption && <div className="mt-1 text-[12px] text-dim">{caption}</div>}
      {children && <div className="mt-1.5 flex flex-col gap-0.5 text-[12px] leading-snug text-dim">{children}</div>}
    </div>
  );
}

/** The small state chip (card row 1). */
export function KpiChip({ state, value, children }: { state: KpiState; value: number | null; children: ReactNode }) {
  return <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${kpiTone(state, value).chip}`}>{children}</span>;
}
